import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Megaphone,
} from "lucide-react";
import { fetchCalendar } from "../../apis/calendar";
import { fetchTasks } from "../../apis/tasks";
import useAutoRefresh from "../../hooks/useAutoRefresh";
import { displayToast } from "../../utils/Toast";
import {
  TYPE_CIRCULAR,
  TYPE_TASK,
  TYPE_TODO,
  dayKey,
  fmtEventTime as fmtTime,
  normalizeEvents,
} from "../../utils/calendarEvents";
import "../../styles/pages/tasks/Calendar.css";

const TYPE_META = {
  [TYPE_TASK]: {
    label: "Tasks",
    single: "Task",
    icon: CheckSquare,
    color: "#2563eb",
  },
  [TYPE_TODO]: {
    label: "Todos",
    single: "Todo",
    icon: CalendarDays,
    color: "#16a34a",
  },
  [TYPE_CIRCULAR]: {
    label: "Circulars",
    single: "Circular",
    icon: Megaphone,
    color: "#f97316",
  },
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const capitalize = (value) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : "";

const hourOf = (time) => Number(String(time).split(":")[0]) || 0;

const fmtHour = (hour) => {
  const suffix = hour >= 12 ? "PM" : "AM";
  return `${hour % 12 === 0 ? 12 : hour % 12} ${suffix}`;
};

const currentUserName = () =>
  (localStorage.getItem("user_name") || "").trim().toLowerCase();

const isMe = (name) =>
  Boolean(name) && String(name).trim().toLowerCase() === currentUserName();

const ownershipLabel = (event, assignedByMe) => {
  if (event.type === TYPE_CIRCULAR) {
    return event.assignee ? `From ${event.assignee}` : "Circular";
  }
  if (event.type === TYPE_TODO) {
    return event.priority
      ? `${capitalize(event.priority)} priority`
      : "My todo";
  }
  if (assignedByMe) {
    return event.assigneeName
      ? `Assigned by me · to ${event.assigneeName}`
      : "Assigned by me";
  }
  return event.assignerName
    ? `Assigned to me · by ${event.assignerName}`
    : "Assigned to me";
};

const eventSubtitle = (event, assignedByMe) => {
  const parts = [TYPE_META[event.type].single];
  if (event.type === TYPE_CIRCULAR) {
    parts.push(event.isRead === false ? "Unread" : "Read");
  } else if (event.type === TYPE_TODO && event.priority) {
    parts.push(`${capitalize(event.priority)} Priority`);
  } else if (event.type === TYPE_TASK) {
    parts.push(ownershipLabel(event, assignedByMe));
  }
  return parts.join(" · ");
};

const eventBadge = (event) => {
  if (event.type === TYPE_CIRCULAR) {
    return event.isRead === false ? { label: "Unread", tone: "orange" } : null;
  }
  if (event.completed) return { label: "Done", tone: "green" };
  if (event.overdue) return { label: "Overdue", tone: "red" };
  return null;
};

const DAY_START_HOUR = 8;
const DAY_END_HOUR = 18;

const CalendarView = ({ onOpenEvent }) => {
  const today = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState(
    () => new Date(today.getFullYear(), today.getMonth(), 1),
  );
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTypes, setActiveTypes] = useState([
    TYPE_TASK,
    TYPE_TODO,
    TYPE_CIRCULAR,
  ]);
  const [selectedDay, setSelectedDay] = useState(() => dayKey(today));
  const [byMeKeys, setByMeKeys] = useState(() => new Set());

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetchTasks({ view: "by_me", count: 200 });
        if (!active || !Array.isArray(res?.items)) return;
        const keys = new Set();
        res.items.forEach((task) => {
          if (task?.id != null) keys.add(`id:${task.id}`);
          if (task?.title) keys.add(`title:${task.title.trim().toLowerCase()}`);
        });
        setByMeKeys(keys);
      } catch (e) {
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const isAssignedByMe = useCallback(
    (event) => {
      if (event.assignerName) return isMe(event.assignerName);
      if (event.rawId != null && byMeKeys.has(`id:${event.rawId}`)) return true;
      return byMeKeys.has(`title:${String(event.title).trim().toLowerCase()}`);
    },
    [byMeKeys],
  );

  const monthStart = useMemo(
    () => new Date(cursor.getFullYear(), cursor.getMonth(), 1),
    [cursor],
  );
  const monthEnd = useMemo(
    () => new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0),
    [cursor],
  );

  const loadCalendar = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const res = await fetchCalendar({
          from: dayKey(monthStart),
          to: dayKey(monthEnd),
          month: monthStart.getMonth() + 1,
          year: monthStart.getFullYear(),
        });
        if (res?.error) {
          if (!silent) {
            displayToast(
              res?.error?.description || "Failed to load calendar",
              "error",
            );
          }
          setEvents([]);
          return;
        }
        setEvents(normalizeEvents(res));
      } catch (e) {
        if (!silent) displayToast("Failed to load calendar", "error");
        setEvents([]);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [monthStart, monthEnd],
  );

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  useAutoRefresh(() => loadCalendar(true));

  const visibleEvents = useMemo(
    () => events.filter((e) => activeTypes.includes(e.type)),
    [events, activeTypes],
  );

  const eventsByDay = useMemo(() => {
    const map = new Map();
    visibleEvents.forEach((e) => {
      if (!map.has(e.day)) map.set(e.day, []);
      map.get(e.day).push(e);
    });
    map.forEach((list) =>
      list.sort((a, b) => (a.time || "99:99").localeCompare(b.time || "99:99")),
    );
    return map;
  }, [visibleEvents]);

  const gridDays = useMemo(() => {
    const start = new Date(monthStart);
    start.setDate(start.getDate() - start.getDay());
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [monthStart]);

  const toggleType = (type) =>
    setActiveTypes((prev) =>
      prev.length === 1 && prev[0] === type
        ? prev
        : prev.includes(type)
          ? prev.filter((t) => t !== type)
          : [...prev, type],
    );

  const showOnly = (type) => setActiveTypes([type]);

  const goToday = () => {
    const now = new Date();
    setCursor(new Date(now.getFullYear(), now.getMonth(), 1));
    setSelectedDay(dayKey(now));
  };

  const shiftMonth = (delta) =>
    setCursor(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1),
    );

  const selectedEvents = useMemo(
    () => eventsByDay.get(selectedDay) || [],
    [eventsByDay, selectedDay],
  );
  const todayKey = dayKey(today);
  const agenda = useMemo(() => {
    const allDay = selectedEvents.filter((e) => !e.time);
    const timed = selectedEvents.filter((e) => e.time);
    const hours = timed.map((e) => hourOf(e.time));
    const start = Math.min(DAY_START_HOUR, ...hours);
    const end = Math.max(DAY_END_HOUR, ...hours);
    const rows = [];
    for (let hour = start; hour <= end; hour += 1) {
      rows.push({ hour, items: timed.filter((e) => hourOf(e.time) === hour) });
    }
    return { allDay, rows };
  }, [selectedEvents]);

  const counts = useMemo(() => {
    const base = { [TYPE_TASK]: 0, [TYPE_TODO]: 0, [TYPE_CIRCULAR]: 0 };
    events.forEach((e) => {
      base[e.type] = (base[e.type] || 0) + 1;
    });
    return base;
  }, [events]);

  const renderAgendaEvent = (event) => {
    const meta = TYPE_META[event.type];
    const Icon = meta.icon;
    const badge = eventBadge(event);
    const assignedByMe = isAssignedByMe(event);
    const who = ownershipLabel(event, assignedByMe);
    return (
      <button
        type="button"
        key={event.id}
        className={`cal-agenda-event ${event.completed ? "done" : ""}`}
        style={{ "--chip-color": meta.color }}
        onClick={() => onOpenEvent?.({ ...event, assignedByMe })}
        title={`${event.title} — ${eventSubtitle(event, assignedByMe)}`}
      >
        <span className="cal-agenda-event-icon">
          <Icon size={13} />
        </span>
        <span className="cal-agenda-event-body">
          <span className="cal-agenda-event-title">{event.title}</span>
          {who && (
            <span
              className={`cal-agenda-event-who ${
                event.type === TYPE_TASK
                  ? assignedByMe
                    ? "by-me"
                    : "to-me"
                  : ""
              }`}
            >
              {who}
            </span>
          )}
        </span>
        <span className="cal-agenda-event-side">
          {event.time && (
            <span className="cal-agenda-event-time">{fmtTime(event.time)}</span>
          )}
          {badge && (
            <span className={`cal-agenda-badge ${badge.tone}`}>
              {badge.label}
            </span>
          )}
        </span>
        <ChevronRight size={14} className="cal-agenda-event-arrow" />
      </button>
    );
  };

  return (
    <section className="cal-board">
      <div className="cal-head">
        <div>
          <h2 className="cal-title">Calendar</h2>
          <p className="cal-sub">Tasks, Todos &amp; Circulars in one place</p>
        </div>
        <div className="cal-filters">
          {Object.entries(TYPE_META).map(([type, meta]) => {
            const Icon = meta.icon;
            const active = activeTypes.includes(type);
            return (
              <button
                key={type}
                type="button"
                className={`cal-chip ${active ? "active" : ""}`}
                style={{ "--chip-color": meta.color }}
                aria-pressed={active}
                onClick={() => toggleType(type)}
                onDoubleClick={() => showOnly(type)}
                title={`Show ${meta.label} only (double click)`}
              >
                <Icon size={14} />
                {meta.label}
                <span className="cal-chip-count">{counts[type] || 0}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="cal-nav">
        <button
          type="button"
          className="cal-nav-btn"
          onClick={() => shiftMonth(-1)}
          aria-label="Previous month"
        >
          <ChevronLeft size={18} />
        </button>
        <button type="button" className="cal-today-btn" onClick={goToday}>
          Today
        </button>
        <h3 className="cal-month">
          {monthStart.toLocaleDateString([], {
            month: "long",
            year: "numeric",
          })}
        </h3>
        <button
          type="button"
          className="cal-nav-btn"
          onClick={() => shiftMonth(1)}
          aria-label="Next month"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {loading ? (
        <div className="cal-state">
          <Loader2 className="cal-spin" size={26} />
          <p>Loading calendar…</p>
        </div>
      ) : (
        <div className="cal-layout">
          <div className="cal-grid-wrap">
            <div className="cal-weekdays">
              {WEEKDAYS.map((d) => (
                <span key={d}>{d}</span>
              ))}
            </div>
            <div className="cal-grid">
              {gridDays.map((date) => {
                const key = dayKey(date);
                const inMonth = date.getMonth() === monthStart.getMonth();
                const dayEvents = eventsByDay.get(key) || [];
                return (
                  <button
                    type="button"
                    key={key}
                    className={`cal-cell ${inMonth ? "" : "muted"} ${
                      key === todayKey ? "today" : ""
                    } ${key === selectedDay ? "selected" : ""}`}
                    onClick={() => setSelectedDay(key)}
                  >
                    <span className="cal-cell-date">{date.getDate()}</span>
                    <span className="cal-cell-events">
                      {dayEvents.slice(0, 2).map((e) => (
                        <span
                          key={e.id}
                          className="cal-event-pill"
                          style={{ "--chip-color": TYPE_META[e.type].color }}
                          title={e.title}
                        >
                          {e.time ? `${fmtTime(e.time)} · ` : ""}
                          {e.title}
                        </span>
                      ))}
                      {dayEvents.length > 2 && (
                        <span className="cal-more">
                          +{dayEvents.length - 2} more
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <aside className="cal-side">
            <div className="cal-side-head">
              <CalendarDays size={16} />
              <div>
                <b>
                  {new Date(`${selectedDay}T00:00:00`).toLocaleDateString([], {
                    weekday: "long",
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </b>
                <small>
                  {selectedEvents.length}{" "}
                  {selectedEvents.length === 1 ? "entry" : "entries"}
                </small>
              </div>
            </div>
            {selectedEvents.length === 0 ? (
              <div className="cal-side-empty">
                <CalendarDays size={26} />
                <p>Nothing scheduled for this day.</p>
              </div>
            ) : (
              <div className="cal-agenda">
                {agenda.allDay.length > 0 && (
                  <div className="cal-agenda-row all-day">
                    <div className="cal-agenda-rowhead">
                      <span className="cal-agenda-hour">All Day</span>
                      <span className="cal-agenda-rule" />
                    </div>
                    <div className="cal-agenda-slot">
                      {agenda.allDay.map(renderAgendaEvent)}
                    </div>
                  </div>
                )}
                {agenda.rows.map((row) => (
                  <div
                    className={`cal-agenda-row ${row.items.length ? "filled" : ""}`}
                    key={row.hour}
                  >
                    <div className="cal-agenda-rowhead">
                      <span className="cal-agenda-hour">
                        {fmtHour(row.hour)}
                      </span>
                      <span className="cal-agenda-rule" />
                    </div>
                    {row.items.length > 0 && (
                      <div className="cal-agenda-slot">
                        {row.items.map(renderAgendaEvent)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </aside>
        </div>
      )}
    </section>
  );
};

export default CalendarView;
